import { buttonVariants } from "@foreday/ui/components/button-variants";
import { CaretLeftIcon } from "@foreday/ui/components/icons";
import { cn } from "@foreday/ui/lib/utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { Page } from "@/client/components/page";
import { PagePending } from "@/client/components/page-pending";
import { ScreenHeading } from "@/client/components/screen-heading";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { ensureTenantData, requireModule } from "@/client/lib/session";
import {
  MyPayslips,
  MyPayslipsSkeleton,
} from "@/features/payslips/components/my-payslips";
import { payslipsCopy } from "@/features/payslips/copy";
import { mySlipsQuery } from "@/features/payslips/payslips-query";

const back = buttonVariants({ variant: "ghost", size: "sm" });

/** An Admin's own slips; anybody else already has them on Payslips. */
export const Route = createFileRoute("/_authenticated/$slug/payslips/mine")({
  beforeLoad: ({ context, params }) => {
    requireModule(context, "payslips");
    if (context.role !== "admin") {
      throw redirect({ to: "/$slug/payslips", params, replace: true });
    }
  },
  loader: ({ context }) => ensureTenantData(context.queryClient, mySlipsQuery),
  head: async ({ match }) => {
    const { payslipsCopy } = await import("@/features/payslips/copy");
    return {
      meta: [
        {
          title: pageTitle(payslipsCopy.mine.title, match.context.companyName),
        },
      ],
    };
  },
  pendingMs: PENDING_MS,
  pendingComponent: () => (
    <PagePending shape="list" className="min-h-full" header={<Header />}>
      <MyPayslipsSkeleton />
    </PagePending>
  ),
  component: Mine,
});

function Header() {
  const { slug } = Route.useParams();
  return (
    <>
      <Link
        to="/$slug/payslips"
        params={{ slug }}
        className={cn(back, "self-start")}
      >
        <CaretLeftIcon aria-hidden="true" />
        {payslipsCopy.title}
      </Link>
      <ScreenHeading className="font-display text-display">
        {payslipsCopy.mine.title}
      </ScreenHeading>
    </>
  );
}

function Mine() {
  const { slug } = Route.useParams();
  const { companyTimezone } = Route.useRouteContext();
  const { data } = useSuspenseQuery(mySlipsQuery);
  return (
    <Page shape="list" className="min-h-full">
      <Header />
      <MyPayslips slug={slug} slips={data} timeZone={companyTimezone} />
    </Page>
  );
}
